import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Amount } from '../../../web/src/components/display/amount.jsx';
import { useSiteLang } from '../layout/use-site-lang.js';
import { portfolioData, getCurrencyInfo } from './report-utils.js';

/**
 * Portfolio report card — investment and pension accounts with cost basis,
 * gain/loss, and return %, computed from the mock accounts.
 * @returns {JSX.Element}
 */
export function PortfolioCard() {
	const { t } = useTranslation();
	const lang = useSiteLang();
	const rows = useMemo(() => portfolioData(lang), [lang]);

	/**
	 * @param {number} amount
	 * @param {string} code
	 * @returns {import('react').ReactNode}
	 */
	const amt = (amount, code) => {
		const info = getCurrencyInfo(code, lang);
		if (!info) { return String(amount); }
		return <Amount value={amount} decimalPlaces={info.decimalPlaces} symbol={info.symbol} />;
	};

	/**
	 * @param {number|null} diff
	 * @param {string} code
	 * @returns {import('react').ReactNode}
	 */
	const gain = (diff, code) => {
		if (diff === null) { return '\u2014'; }
		return <>{diff >= 0 ? '+' : ''}{amt(diff, code)}</>;
	};

	/**
	 * @param {number|null} pct
	 * @returns {string}
	 */
	const ret = (pct) => (pct === null ? '\u2014' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`);

	return (
		<div className="card report-card">
			<div className="card-header">
				<h2>{t('report.portfolio')}</h2>
				<span className="row-count">{t('report.accountCount', { count: rows.length })}</span>
			</div>
			<div className="card-body report-body">
				<div className="report-table-wrap"><table className="report-table">
					<thead>
						<tr>
							<th>{t('report.colName')}</th>
							<th className="right">{t('report.colPrincipal')}</th>
							<th className="right">{t('report.colValue')}</th>
							<th className="right">{t('report.colGainLoss')}</th>
							<th className="right">{t('report.colReturn')}</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((r) => (
							<tr key={r.account.id}>
								<td>{r.account.name}</td>
								<td className="right">{r.account.principal !== null ? amt(r.account.principal, r.account.currency) : '\u2014'}</td>
								<td className="right">{amt(r.account.balance, r.account.currency)}</td>
								<td className={`right ${r.diff !== null ? (r.diff >= 0 ? 'report-profit' : '') : ''}`}>
									{gain(r.diff, r.account.currency)}
								</td>
								<td className={`right ${r.diff !== null ? (r.diff >= 0 ? 'report-profit' : '') : ''}`}>
									{ret(r.pct)}
								</td>
							</tr>
						))}
					</tbody>
				</table></div>
			</div>
		</div>
	);
}
